import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import "./styles/Home.css";

const Home = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${apiUrl}/products`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Erreur lors de la récupération des produits");
        }

        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error("Erreur lors du chargement des produits :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [apiUrl]);

  return (
    <main className="home">
      <Helmet>
        <title>WinlyDeal - Contribuez et remportez des produits d'exception</title>
        <meta 
          name="description" 
          content="Participez aux meilleurs deals sur WinlyDeal. Contribuez pour remporter des produits high-tech, des cadeaux, et bien plus. Gagnez en participant dès aujourd'hui !" 
        />
        <meta 
          name="keywords" 
          content="WinlyDeal, deals, produits, contributions, remporter, gagner, high-tech, cadeaux, plateforme, lot, participation, tirage" 
        />
        <link rel="canonical" href="https://www.winlydeal.com" />
        <meta name="robots" content="index, follow" />

        <meta property="og:title" content="WinlyDeal - Gagnez des produits uniques en contribuant" />
        <meta property="og:description" content="Découvrez WinlyDeal : participez à des deals exclusifs et remportez vos produits préférés en contribuant." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.winlydeal.com" />
        <meta property="og:image" content="https://www.winlydeal.com/images/og-image.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="WinlyDeal - Remportez des produits d'exception" />
        <meta name="twitter:description" content="Participez à WinlyDeal pour remporter vos produits préférés grâce à votre contribution !" />
        <meta name="twitter:image" content="https://www.winlydeal.com/images/og-image.jpg" />

        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "WinlyDeal",
              "url": "https://www.winlydeal.com",
              "logo": "https://www.winlydeal.com/images/og-image.jpg",
              "sameAs": [
                "https://www.facebook.com/winlydeal",
                "https://www.instagram.com/winlydeal"
              ],
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@winlydeal.com",
                "contactType": "Customer Support"
              }
            }
          `}
        </script>

        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "AggregateRating",
              "itemReviewed": {
                "@type": "Organization",
                "name": "WinlyDeal"
              },
              "ratingValue": "4.8",
              "reviewCount": "120",
              "bestRating": "5",
              "worstRating": "1"
            }
          `}
        </script>
      </Helmet>

      <section className="home-products">
        <h1 className="home-title">Contribuez pour gagner des produits exceptionnels</h1>
        <p className="home-subtitle">
          Découvrez des deals exclusifs sur WinlyDeal : smartphones, consoles, TV et plus encore. Contribuez et tentez de remporter vos produits préférés !
        </p>

        {loading ? (
          <div className="home-loading">Chargement des produits...</div>
        ) : (
          <div className="home-grid">
            {products.map((product) => (
              <div className="home-product-card" key={product.id}>
                {product.images && product.images.length > 0 ? (
                  <img src={`/images/${product.images[0]}`} alt={product.name} className="home-product-image" />
                ) : (
                  <img src="/images/placeholder.jpg" alt="Non disponible" className="home-product-image" />
                )}
                <h2 className="home-product-name">{product.name}</h2>
                <p className="home-product-description">{product.description}</p>
                <div className="home-product-pricing">
                  <p>Prix : {product.price} €</p>
                  <p>Collecté : {product.collected} €</p>
                </div>
                <div className="home-product-progress">
                  <div
                    className={`home-product-progress-bar ${
                      (product.collected / product.price) < 0.5
                        ? 'low'
                        : (product.collected / product.price) < 0.8
                        ? 'medium'
                        : 'high'
                    }`}
                    style={{ width: `${(product.collected / product.price) * 100}%` }}
                  ></div>
                </div>
                <button
                  className="home-product-button"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  Voir le produit
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="home-explainer">
        <h2 className="home-explainer-title">Comment fonctionne WinlyDeal ?</h2>
        <p className="home-explainer-text">
          WinlyDeal vous permet de participer à des offres exclusives en contribuant pour vos produits préférés. Le plus grand contributeur remporte l'article une fois la collecte terminée !
        </p>
        <ul className="home-explainer-list">
          <li>Sélectionnez un produit parmi les deals disponibles.</li>
          <li>Contribuez en quelques clics pour maximiser vos chances de gagner.</li>
          <li>Le contributeur ayant donné le plus repart avec le produit !</li>
        </ul>
        <button
          className="home-explainer-button"
          onClick={() => navigate('/rules')}
        >
          Lire les règles complètes
        </button>
      </section>
    </main>
  );
};

export default Home;
